import { RowIncidenceDto, RowResponsableTimesDto, RowResumeIdentifierDto, RowResumeIncidenceTimesDto, RowResumeResponsableDto, SummaryResponsableTimeAverageDto, SummaryResponsableTimeDto } from "@/dtos";
import { Administrator, Incidence, IncidenceResume, IncidenceTime, Responsable, Status } from "@/entities";
import { getOriginOption, getPriorityOption } from "@/helpers/options";
import { ResponsableType } from "@/helpers/types";
import { getCurrentDateTime, getHoursDifference, getHoursFromTime, getTimeMomentDifference, parseToHours, parseToLocalDateTimeFromUtc, sumTimes } from "@/helpers/utilities";
import { generateColor, generateRandomColor } from "@/helpers/utilities/colors";
import { BarDataset } from "@/ui/components/graphs/bar/interfaces";
import { PieDataset } from "@/ui/components/graphs/pie/interfaces/pie-dataset.interface";


export const formatRowIncidenceDto = (incidence: Incidence, responsables: Responsable[]): RowIncidenceDto => {
    const responsablesIncidence = responsables.filter(x => x.type == ResponsableType.RESPONSABLE);

    const incidence_status = incidence.incidence_status[0];
    const incidence_date = parseToLocalDateTimeFromUtc(incidence.date_start.toString());
    const incidence_start = parseToLocalDateTimeFromUtc(incidence.created_at?.toString());
    const incidence_status_date = parseToLocalDateTimeFromUtc(incidence_status.created_at?.toString());

    const row: RowIncidenceDto = {
        ...incidence,
        date_label: incidence_date,
        time_passed: incidence_status.status?.is_completed ?
            getTimeMomentDifference(incidence_start, incidence_status_date) :
            getTimeMomentDifference(incidence_date, getCurrentDateTime()),
        status_label: incidence.incidence_status[0].status?.name,
        priority_label: getPriorityOption(incidence.priority).label,
        time_working: incidence.total_time_working,
        responsable: responsablesIncidence.length > 0 ? responsablesIncidence[responsablesIncidence?.length - 1].details?.administrator : 'Sin responsable',
        current_status: incidence.incidence_status[incidence.incidence_status.length - 1]?.status,
        active: incidence.is_active ? 'Activo' : 'Inactivo',
        incidence_label: `(${incidence.code}) ${incidence.name}`,
        origin_label: getOriginOption(incidence.origin).label
    };

    return row;
}

export const formatGraphIncidenceDto = (incidences: SummaryResponsableTimeDto[]): PieDataset => {
    const result = {
        labels: incidences.map(x => x.responsable),
        data: incidences.map(x => x.total_time_minutes),
        backgroundColor: incidences.map(x => generateRandomColor()),
        descriptions: incidences.map(x => ` ${x.total_time} en ${x.total} ${x.total > 1 ? 'incidencias' : 'incidencia'}`)
    } as PieDataset;

    return result;
}

export const formatGraphResponsableDto = (incidences: Incidence[], statuses: Status[]) => {
    const labels: string[] = [];
    const datasets: BarDataset[] = [];
    const dataExport: RowResumeResponsableDto[] = [];

    for (const status of statuses) {
        const dataset: BarDataset = {
            label: status.name,
            data: [],
            backgroundColor: [status.color]
        }

        datasets.push(dataset);
    }

    for (const incidence of incidences) {
        const responsablesIncidence = incidence.responsables?.filter(x => x.type == ResponsableType.RESPONSABLE);
        if (incidence.responsables.length == 0 || responsablesIncidence.length == 0) {
            const label = labels.find(x => x == 'Sin Responsable');
            if (!label) {
                labels.push('Sin Responsable');

                for (const dataset of datasets) {
                    dataset.data[labels.length - 1] = incidence.incidence_status[0].status.name == dataset.label ? 1 : 0;
                    const row: RowResumeResponsableDto = {
                        responsable: 'Sin Responsable',
                        status: dataset.label ?? '',
                        amount: incidence.incidence_status[0].status.name == dataset.label ? 1 : 0
                    }

                    dataExport.push(row);
                }
                continue;
            }

            const index = labels.findIndex(x => x == 'Sin Responsable');
            const dataset = datasets.find(x => x.label == incidence.incidence_status[0].status.name);
            if (dataset) {
                dataset.data[index] += 1;
            }

            const row = dataExport.find(x => x.responsable == 'Sin Responsable' && x.status == incidence.incidence_status[0].status.name);
            if (row) {
                row.amount += 1;
            }

            continue;
        }

        const label = labels.find(x => x == responsablesIncidence[responsablesIncidence.length - 1].details.administrator);
        if (!label) {
            labels.push(responsablesIncidence[responsablesIncidence.length - 1].details.administrator);
            for (const dataset of datasets) {
                dataset.data[labels.length - 1] = incidence.incidence_status[0].status.name == dataset.label ? 1 : 0;
                const row: RowResumeResponsableDto = {
                    responsable: responsablesIncidence[responsablesIncidence.length - 1].details.administrator,
                    status: dataset.label ?? '',
                    amount: incidence.incidence_status[0].status.name == dataset.label ? 1 : 0
                }

                dataExport.push(row);
            }
            continue;
        }

        const index = labels.findIndex(x => x == label);
        const dataset = datasets.find(x => x.label == incidence.incidence_status[0].status.name);
        if (dataset) {
            dataset.data[index] += 1;
        }

        const row = dataExport.find(x => x.responsable == label && x.status == incidence.incidence_status[0].status.name);
        if (row) {
            row.amount += 1;
        }
    }

    return { labels, datasets, dataExport };
}

export const formatGraphIdentifierDto = (incidences: Incidence[]) => {
    const labels: string[] = [];
    const datasets: BarDataset[] = [];
    const dataExport: RowResumeIdentifierDto[] = [];

    for (const incidence of incidences) {
        if (incidence.incidence_identifiers.length == 0) {
            const label = labels.find(x => x == 'Sin etiqueta');
            if (!label) {
                labels.push('Sin etiqueta');
                const dataset: BarDataset = {
                    label: 'Sin etiqueta',
                    data: [],
                    backgroundColor: [generateColor().color]
                }

                if (labels.length > 1) {
                    labels.map((x, i) => {
                        dataset.data[i] = (i != (labels.length - 1)) ? 0 : 1;
                    });
                } else {
                    dataset.data[0] = 1;
                }

                datasets.push(dataset);

                const row: RowResumeIdentifierDto = {
                    identifier: 'Sin etiqueta',
                    amount: 1
                }
                dataExport.push(row);
                continue;
            }

            const dataset = datasets.find(x => x.label == 'Sin etiqueta');
            if (dataset) {
                const index = labels.findIndex(x => x == dataset.label);
                dataset.data.map(x => x = 0);
                dataset.data[index] += 1;
            }

            const row = dataExport.find(x => x.identifier == 'Sin etiqueta');
            if (row) {
                row.amount += 1;
            }

            continue;
        }

        for (const incidenceIdentifier of incidence.incidence_identifiers) {
            const label = labels.find(x => x == (incidenceIdentifier.identifier?.name ?? 'Sin etiqueta'));
            if (!label) {
                labels.push(incidenceIdentifier.identifier.name);

                const dataset: BarDataset = {
                    label: incidenceIdentifier.identifier.name,
                    data: [],
                    backgroundColor: [incidenceIdentifier.identifier.color ?? generateColor().color]
                }

                if (labels.length > 1) {
                    labels.map((x, i) => {
                        dataset.data[i] = (i != (labels.length - 1)) ? 0 : 1;
                    });
                } else {
                    dataset.data[0] = 1;
                }

                datasets.push(dataset);

                const row: RowResumeIdentifierDto = {
                    identifier: incidenceIdentifier.identifier.name,
                    amount: 1
                }
                dataExport.push(row);
            }

            const dataset = datasets.find(x => x.label == label);
            if (dataset) {
                const index = labels.findIndex(x => x == dataset.label);
                dataset.data.map(x => x = 0);
                dataset.data[index] += 1;
            }

            const row = dataExport.find(x => x.identifier == label);
            if (row) {
                row.amount += 1;
            }
        }
    }

    for (const dataset of datasets) {
        for (let i = 0; i < labels.length; i++) {
            if (!dataset.data[i]) {
                dataset.data[i] = 0;
            }
        }
    }

    return { labels, datasets, dataExport };
}

const getHoursFromResume = (incidence_resume: IncidenceResume, incidence_start: string) => {
    if (!incidence_resume.date_updated) {
        return getHoursDifference(incidence_resume.date_created.toString(), getCurrentDateTime());
    }

    return getHoursDifference(incidence_resume.date_created.toString(), incidence_resume.date_updated.toString());
}

export const formatGraphResponsableTimeAverageDto = (incidences: Incidence[], statuses: Status[]) => {
    const labels: string[] = [];
    const datasets: BarDataset[] = [];
    const resumes: SummaryResponsableTimeAverageDto[] = [];
    const dataExport: RowResumeIncidenceTimesDto[] = [];

    for (const status of statuses) {
        if (!status.is_completed) {
            const dataset: BarDataset = {
                label: status.name,
                data: [],
                backgroundColor: [status.color]
            }

            datasets.push(dataset);
        }
    }

    for (const incidence of incidences) {
        for (const resume of incidence.incidence_resumes) {
            const label = labels.find(l => l == resume.responsable.details.administrator);
            if (!label) {
                labels.push(resume.responsable.details.administrator);
            }

            const hours = getHoursFromResume(resume, incidence.date_start.toString());
            const incidenceResume = resumes.find(x =>
                x.responsable_id == resume.responsable.administrator_id &&
                x.status_id == resume.status.id
            );

            if (!incidenceResume) {
                const obj: SummaryResponsableTimeAverageDto = {
                    responsable_id: resume.responsable.administrator_id,
                    responsable: resume.responsable.details.administrator,
                    status_id: resume.status.id,
                    status: resume.status.name,
                    hours: [hours]
                }

                resumes.push(obj);
            } else {
                incidenceResume.hours.push(hours);
            }
        }
    }

    for (let i = 0; i < labels.length; i++) {
        const resumeObjs = resumes.filter(x => x.responsable === labels[i]);
        for (const dataset of datasets) {
            const resumeObj = resumeObjs.find(x => dataset.label === x.status);
            if (resumeObj) {
                let average = 0;
                resumeObj.hours.map(x => average += x);
                average = parseFloat((average / resumeObj.hours.length).toFixed(2));
                dataset.data[i] = average;

                const row: RowResumeIncidenceTimesDto = {
                    responsable: resumeObj.responsable,
                    status: resumeObj.status,
                    hours: average
                }

                dataExport.push(row);
                continue;
            }

            dataset.data[i] = 0;
            const row: RowResumeIncidenceTimesDto = {
                responsable: labels[i],
                status: dataset.label ?? '',
                hours: 0
            }

            dataExport.push(row);
        }
    }

    return { labels, datasets, dataExport };
}

export const formatGraphResponsablesTimeDto = (incidences: Incidence[]) => {
    const labels: string[] = [];
    const datasets: PieDataset[] = [];
    const dataExport: RowResponsableTimesDto[] = [];

    const dataset: PieDataset = {
        data: [],
        backgroundColor: []
    }

    for (const incidence of incidences) {
        for (const responsable of incidence.responsables) {
            const label = labels.find(x => x == responsable.details.administrator);
            const hours = parseFloat(getHoursFromTime(responsable.working_time).toFixed(2));
            if (!label) {
                labels.push(responsable.details.administrator);

                dataset.data.push(hours);
                dataset.backgroundColor?.push(generateColor().color);

                const row: RowResponsableTimesDto = {
                    responsable: responsable.details.administrator,
                    hours: hours
                }
                dataExport.push(row);

                continue;
            }

            const index = labels.findIndex(x => x == responsable.details.administrator);
            dataset.data[index] += hours;

            const indexExport = dataExport.findIndex(x => x.responsable == responsable.details.administrator);
            dataExport[indexExport].hours += hours;
        }
    }

    datasets.push(dataset);
    return { labels, datasets, dataExport };
}

export const formatGraphResponsableTimeDto = (incidences: Incidence[]): BarDataset[] => {
    const dataset: BarDataset = {
        label: 'Incidencias (Horas)',
        data: incidences.map(x => parseToHours(x.responsables[0].working_time)),
        backgroundColor: incidences.map(x => "#0635C9")
    }

    return [dataset];
}

export const formatGraphIncidenceTimeDto = (incidenceTimes: IncidenceTime[], administradors: Administrator[]) => {
    const labels: string[] = [];
    const datasets: PieDataset[] = [];
    const dataExport: RowResponsableTimesDto[] = [];

    const dataset: PieDataset = {
        data: [],
        backgroundColor: []
    }

    for (const incidenceTime of incidenceTimes) {
        const label = labels.find(x => x == incidenceTime.details.administrator);
        const hours = parseFloat(getHoursFromTime(incidenceTime.time_working).toFixed(2));

        if (!label) {
            labels.push(incidenceTime.details.administrator);
            dataset.data.push(hours);

            const admin = administradors.find(x => incidenceTime.details.administrator == x.first_name + " " + x.last_name);
            dataset.backgroundColor?.push(admin?.color ?? '#0635C9');

            const row: RowResponsableTimesDto = {
                responsable: incidenceTime.details.administrator,
                hours: hours
            }
            dataExport.push(row);
            continue;
        }

        const index = labels.findIndex(x => x == incidenceTime.details.administrator);
        dataset.data[index] += hours;

        const indexExport = dataExport.findIndex(x => x.responsable == incidenceTime.details.administrator);
        dataExport[indexExport].hours += hours;
    }

    datasets.push(dataset);
    return { labels, datasets, dataExport };
}
